<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import Avatar from '@/components/Avatar.vue'
import Button from '@/components/Button.vue'
import Modal from '@/components/Modal.vue'
import AddOperator from '@/views/settings/agents/AddOperator.vue'
import EditOperator from '@/views/settings/agents/EditOperator.vue'
import { useAlert } from '@/composables/useToast'
import { getAccount, updateAccount } from '@/api/accounts'

const store = useStore()
const { t } = useI18n()

const searchQuery = ref('')
const showAddModal = ref(false)
const showEditModal = ref(false)
const showDeleteModal = ref(false)
const selectedOperator = ref(null)

const agentList = computed(() => store.getters['agents/getAgents'])
const uiFlags = computed(() => store.getters['agents/getUIFlags'])
const currentUserId = computed(() => store.getters['auth/getCurrentUser']?.id)

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return agentList.value
  return agentList.value.filter(
    agent =>
      agent.name.toLowerCase().includes(query) ||
      agent.email.toLowerCase().includes(query)
  )
})

const getAgentRoleName = agent =>
  t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`)

const agentCountLabel = computed(() =>
  t('AGENT_MGMT.COUNT', agentList.value.length, {
    named: { n: agentList.value.length },
  })
)

const canModify = agent => agent.id !== currentUserId.value

const openEdit = agent => {
  selectedOperator.value = agent
  showEditModal.value = true
}

const openDelete = agent => {
  selectedOperator.value = agent
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  try {
    await store.dispatch('agents/delete', selectedOperator.value.id)
    useAlert(t('AGENT_MGMT.DELETE.API.SUCCESS'))
  } catch (error) {
    useAlert(t('AGENT_MGMT.DELETE.API.ERROR'))
  } finally {
    showDeleteModal.value = false
  }
}

const accountId = computed(
  () => store.getters['auth/getCurrentUser']?.accounts?.[0]?.id
)
const isAdministrator = computed(
  () => store.getters['auth/getCurrentUser']?.role === 'administrator'
)
const openLimit = ref('')
const savingLimit = ref(false)

const loadLimit = async () => {
  if (!accountId.value) return
  try {
    const { data } = await getAccount(accountId.value)
    openLimit.value = data.max_open_conversations
  } catch (error) {
    openLimit.value = ''
  }
}

const saveLimit = async () => {
  const value = Number(openLimit.value)
  if (!Number.isInteger(value) || value <= 0 || savingLimit.value) return

  savingLimit.value = true
  try {
    await updateAccount(accountId.value, { max_open_conversations: value })
    useAlert(t('AGENT_MGMT.OPEN_LIMIT.SAVED'))
  } catch (error) {
    useAlert(t('AGENT_MGMT.OPEN_LIMIT.ERROR'))
  } finally {
    savingLimit.value = false
  }
}

onMounted(() => {
  store.dispatch('agents/get')
  loadLimit()
})
</script>

<template>
  <div
    class="flex flex-col w-full h-full px-6 pt-4 pb-8 m-0 overflow-auto bg-n-surface-1"
  >
    <div class="flex items-start w-full mx-auto max-w-5xl">
      <div class="flex flex-col w-full h-full gap-4 font-inter">
        <div class="flex flex-col gap-2">
          <h1 class="text-heading-1 text-n-slate-12">
            {{ $t('AGENT_MGMT.HEADER') }}
          </h1>
          <p class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.DESCRIPTION') }}
          </p>
        </div>

        <div
          v-if="isAdministrator"
          class="flex flex-wrap items-end gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <label class="flex flex-col gap-1.5">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('AGENT_MGMT.OPEN_LIMIT.LABEL') }}
            </span>
            <input
              v-model="openLimit"
              type="number"
              min="1"
              class="w-24 h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
            />
          </label>
          <Button
            sm
            :label="$t('AGENT_MGMT.OPEN_LIMIT.SAVE')"
            :disabled="savingLimit"
            @click="saveLimit"
          />
          <p class="w-full text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.OPEN_LIMIT.SUB_TEXT') }}
          </p>
        </div>

        <div class="flex items-center justify-between gap-4">
          <div class="relative w-72">
            <span
              class="absolute -translate-y-1/2 left-3 top-1/2 size-4 i-lucide-search text-n-slate-10"
            />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
              class="w-full py-2 pr-3 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 pl-9 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            />
          </div>
          <div class="flex items-center gap-3 min-w-0">
            <span
              v-if="agentList.length"
              class="text-body-main text-n-slate-11"
            >
              {{ agentCountLabel }}
            </span>
            <div
              v-if="agentList.length"
              class="flex-shrink-0 w-px h-3 rounded-lg bg-n-weak ltr:ml-1 ltr:mr-2 rtl:ml-2 rtl:mr-1"
            />
            <Button
              sm
              :label="$t('AGENT_MGMT.ADD.BUTTON')"
              @click="showAddModal = true"
            />
          </div>
        </div>

        <div>
          <div
            v-if="uiFlags.isFetching"
            class="flex items-center justify-center py-20 text-body-main text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.LOADING') }}
          </div>

          <span
            v-else-if="!filteredAgentList.length && searchQuery"
            class="flex items-center justify-center py-20 text-body-main !text-base text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.NO_RESULTS') }}
          </span>

          <span
            v-else-if="!agentList.length"
            class="flex items-center justify-center py-20 text-body-main !text-base text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.LIST.404') }}
          </span>

          <div v-else class="border-t divide-y divide-n-weak border-n-weak">
            <div
              v-for="agent in filteredAgentList"
              :key="agent.email"
              class="flex flex-row items-start justify-between gap-4 py-4"
            >
              <div class="flex items-center gap-4">
                <Avatar
                  :name="agent.name"
                  :size="40"
                  :status="agent.availability || 'offline'"
                  :src="agent.avatar_url"
                  logo-fallback
                />
                <div class="flex flex-col items-start gap-1.5">
                  <span class="block capitalize text-heading-3 text-n-slate-12">
                    {{ agent.name }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="text-body-main text-n-slate-11">
                      {{ agent.email }}
                    </span>
                    <div class="w-px h-3 rounded-lg bg-n-strong" />
                    <span class="text-body-main text-n-slate-11">
                      {{ getAgentRoleName(agent) }}
                    </span>
                    <div class="w-px h-3 rounded-lg bg-n-strong" />
                    <span class="text-body-main text-n-slate-11">
                      {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="canModify(agent)" class="flex items-center gap-2">
                <Button
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON')"
                  slate
                  sm
                  icon="i-mk-edit-pen"
                  :aria-label="$t('AGENT_MGMT.EDIT.BUTTON')"
                  @click="openEdit(agent)"
                />
                <Button
                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON')"
                  slate
                  sm
                  icon="i-mk-bin"
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :aria-label="$t('AGENT_MGMT.DELETE.BUTTON')"
                  @click="openDelete(agent)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      :show="showAddModal"
      :title="$t('AGENT_MGMT.ADD.TITLE')"
      :description="$t('AGENT_MGMT.ADD.DESC')"
      @close="showAddModal = false"
    >
      <AddOperator @close="showAddModal = false" />
    </Modal>

    <Modal
      :show="showEditModal"
      :title="$t('AGENT_MGMT.EDIT.TITLE')"
      @close="showEditModal = false"
    >
      <EditOperator
        v-if="selectedOperator"
        :operator="selectedOperator"
        @close="showEditModal = false"
      />
    </Modal>

    <Modal
      :show="showDeleteModal"
      :title="$t('AGENT_MGMT.DELETE.TITLE')"
      @close="showDeleteModal = false"
    >
      <div class="flex flex-col gap-6">
        <p class="text-body-main text-n-slate-11">
          {{
            $t('AGENT_MGMT.DELETE.CONFIRM', { name: selectedOperator?.name })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            :label="$t('AGENT_MGMT.DELETE.CANCEL')"
            @click="showDeleteModal = false"
          />
          <Button
            ruby
            :label="$t('AGENT_MGMT.DELETE.SUBMIT')"
            :disabled="uiFlags.isDeleting"
            @click="confirmDelete"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>
